import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ShoppingCart, Heart, User, Menu, X, LogOut, LayoutDashboard, Globe, ChevronDown, ShieldCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { useI18n } from "../i18n";
import { Button } from "./ui/button";
import BrandMark from "./BrandMark";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export default function Header() {
  const { user, logout } = useAuth();
  const { itemCount, wishlist } = useCart();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { to: "/catalog?type=game", label: t("nav.games") },
    { to: "/catalog?type=gift_card", label: t("nav.gift_cards") },
    { to: "/catalog?type=subscription", label: t("nav.subscriptions") },
    { to: "/catalog?flash=1", label: t("nav.deals") },
    { to: "/catalog?new=1", label: t("nav.new_releases") },
    { to: "/membership", label: "Premium", highlight: true },
  ];

  const onSearchSubmit = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/search?q=${encodeURIComponent(search)}`);
  };

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled ? "glass-light shadow-[0_2px_24px_rgba(0,0,0,0.06)]" : "bg-white/80 backdrop-blur-md"
      }`}
      data-testid="site-header"
    >
      <div className="gf-container flex items-center justify-between gap-4 h-[72px]">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
          <BrandMark size="md" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1" data-testid="main-nav">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                l.highlight
                  ? "text-[#A855F7] hover:bg-purple-50 font-semibold"
                  : "text-slate-700 hover:text-[#A855F7] hover:bg-purple-50"
              }`}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        {/* Search */}
        <form onSubmit={onSearchSubmit} className="hidden md:flex flex-1 max-w-sm" data-testid="search-form">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("common.search_placeholder")}
              className="w-full pl-10 pr-4 h-10 rounded-full bg-slate-100/80 border border-transparent focus:border-[#A855F7]/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#A855F7]/20 text-sm"
              data-testid="search-input"
            />
          </div>
        </form>

        {/* Right actions */}
        <div className="flex items-center gap-1">
          {/* Lang */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="hidden sm:flex items-center gap-1 px-2 py-2 text-xs font-medium text-slate-600 hover:text-[#A855F7] rounded-lg hover:bg-purple-50 transition-colors" data-testid="lang-toggle">
                <Globe className="w-4 h-4" />
                {lang.toUpperCase()}
                <ChevronDown className="w-3 h-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" data-testid="lang-menu">
              <DropdownMenuItem onClick={() => setLang("pt")} data-testid="lang-pt">🇧🇷 Português</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLang("en")} data-testid="lang-en">🇺🇸 English</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Link to="/wishlist" className="relative p-2 hover:bg-purple-50 rounded-lg transition-colors" data-testid="wishlist-link">
            <Heart className="w-5 h-5 text-slate-700" />
            {wishlist.length > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-[#A855F7] text-white text-[10px] font-semibold w-4 h-4 rounded-full grid place-items-center">
                {wishlist.length}
              </span>
            )}
          </Link>

          <Link to="/cart" className="relative p-2 hover:bg-purple-50 rounded-lg transition-colors" data-testid="cart-link">
            <ShoppingCart className="w-5 h-5 text-slate-700" />
            {itemCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-[#A855F7] text-white text-[10px] font-semibold min-w-[16px] h-4 px-1 rounded-full grid place-items-center">
                {itemCount}
              </span>
            )}
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 p-1.5 hover:bg-purple-50 rounded-lg transition-colors" data-testid="user-menu-trigger">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#A855F7] to-[#7E22CE] grid place-items-center text-white text-sm font-semibold">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" data-testid="user-menu">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold">{user.name}</div>
                  <div className="text-xs text-slate-500">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                  <LayoutDashboard className="w-4 h-4 mr-2" /> {t("common.dashboard")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/dashboard/orders")} data-testid="menu-orders">
                  <ShoppingCart className="w-4 h-4 mr-2" /> {t("common.orders")}
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <ShieldCheck className="w-4 h-4 mr-2" /> {t("common.admin")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> {t("common.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" className="hidden sm:block" data-testid="login-link">
              <Button variant="ghost" size="sm" className="text-slate-700">
                <User className="w-4 h-4 mr-1.5" />
                {t("common.login")}
              </Button>
            </Link>
          )}

          <button
            className="lg:hidden p-2 hover:bg-purple-50 rounded-lg"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-white border-t border-slate-100"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              <form onSubmit={onSearchSubmit} className="md:hidden mb-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t("common.search_placeholder")}
                    className="w-full pl-10 pr-4 h-10 rounded-full bg-slate-100 border-transparent focus:bg-white focus:outline-none text-sm"
                  />
                </div>
              </form>
              {navLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setMobileOpen(false)}
                  className="py-2.5 text-sm font-medium text-slate-800 hover:text-[#A855F7]"
                >
                  {l.label}
                </Link>
              ))}
              {!user && (
                <Link to="/login" onClick={() => setMobileOpen(false)} className="py-2.5 text-sm font-semibold text-[#A855F7]">
                  {t("common.login")} / {t("common.register")}
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
